import React, { useEffect, useRef, useState } from 'react';
import './FewestTurnsGenerator.css';

const DIRS = [
  { dr: -1, dc: 0 },
  { dr: 1, dc: 0 },
  { dr: 0, dc: -1 },
  { dr: 0, dc: 1 }
];

function mulberry32(seed) {
  let a = seed >>> 0;
  return function rand() {
    let t = a += 0x6D2B79F5;
    t = Math.imul(t ^ t >>> 15, t | 1);
    t ^= t + Math.imul(t ^ t >>> 7, t | 61);
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}

function seededInt(rng, min, max) {
  return Math.floor(rng() * (max - min + 1)) + min;
}

function shuffleInPlace(rng, arr) {
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function inBounds(size, r, c) {
  return r >= 0 && c >= 0 && r < size && c < size;
}

function cellKey(r, c) {
  return `${r},${c}`;
}

function nextDirs(prevDir) {
  if (prevDir === -1) return [0, 1, 2, 3];
  if (prevDir === 0 || prevDir === 1) return [2, 3];
  return [0, 1];
}

function buildPath(rng, size, turns, maxTries = 200, maxNodes = 20000) {
  const maxLength = size * size - 1;
  if (turns > maxLength || turns < 1) return null;

  for (let attempt = 0; attempt < maxTries; attempt += 1) {
    let nodesVisited = 0;
    const start = {
      r: seededInt(rng, 0, size - 1),
      c: seededInt(rng, 0, size - 1)
    };
    const path = [start];
    const visited = new Set([cellKey(start.r, start.c)]);

    const dfs = (cur, prevDir, remaining) => {
      nodesVisited += 1;
      if (nodesVisited > maxNodes) return false;
      if (remaining === 0) return true;

      const candidates = [];
      const allowedDirs = nextDirs(prevDir);
      for (const dirIdx of allowedDirs) {
        const dir = DIRS[dirIdx];
        for (let step = 1; step <= size - 1; step += 1) {
          const nr = cur.r + dir.dr * step;
          const nc = cur.c + dir.dc * step;
          if (!inBounds(size, nr, nc)) break;
          const key = cellKey(nr, nc);
          if (!visited.has(key)) {
            candidates.push({ r: nr, c: nc, dir: dirIdx });
          }
        }
      }
      if (candidates.length === 0) return false;
      shuffleInPlace(rng, candidates);

      for (const next of candidates) {
        const key = cellKey(next.r, next.c);
        visited.add(key);
        path.push(next);
        if (dfs(next, next.dir, remaining - 1)) return true;
        path.pop();
        visited.delete(key);
      }
      return false;
    };

    if (dfs(start, -1, turns)) {
      return path;
    }
  }
  return null;
}

function createGrid(size) {
  return Array.from({ length: size }, () => Array(size).fill(null));
}

function solvePuzzle(grid, start, end) {
  const size = grid.length;
  const dirStates = 5;
  const totalStates = size * size * dirStates;
  const dist = Array(totalStates).fill(Infinity);
  const count = Array(totalStates).fill(0);
  const parent = Array(totalStates).fill(null);
  const toIndex = (r, c, dir) => (r * size + c) * dirStates + dir;
  const fromIndex = (idx) => {
    const cell = Math.floor(idx / dirStates);
    const dir = idx % dirStates;
    return { r: Math.floor(cell / size), c: cell % size, dir };
  };

  const startIdx = toIndex(start.r, start.c, 4);
  dist[startIdx] = 0;
  count[startIdx] = 1;
  const queue = [startIdx];
  let head = 0;

  while (head < queue.length) {
    const curIdx = queue[head++];
    const cur = fromIndex(curIdx);
    const steps = grid[cur.r][cur.c];
    if (!steps) continue;
    const allowedDirs = nextDirs(cur.dir === 4 ? -1 : cur.dir);
    for (const dirIdx of allowedDirs) {
      const dir = DIRS[dirIdx];
      const nr = cur.r + dir.dr * steps;
      const nc = cur.c + dir.dc * steps;
      if (!inBounds(size, nr, nc)) continue;
      const nextIdx = toIndex(nr, nc, dirIdx);
      if (dist[nextIdx] === Infinity) {
        dist[nextIdx] = dist[curIdx] + 1;
        count[nextIdx] = count[curIdx];
        parent[nextIdx] = curIdx;
        queue.push(nextIdx);
      } else if (dist[nextIdx] === dist[curIdx] + 1) {
        count[nextIdx] += count[curIdx];
      }
    }
  }

  const endStates = [0, 1, 2, 3].map((dir) => toIndex(end.r, end.c, dir));
  let shortest = Infinity;
  for (const idx of endStates) {
    if (dist[idx] < shortest) shortest = dist[idx];
  }
  let totalCount = 0;
  for (const idx of endStates) {
    if (dist[idx] === shortest) totalCount += count[idx];
  }
  let path = null;
  if (shortest < Infinity && totalCount === 1) {
    const idxPath = [];
    let endIdx = null;
    for (const idx of endStates) {
      if (dist[idx] === shortest && count[idx] === 1) {
        endIdx = idx;
        break;
      }
    }
    let cur = endIdx;
    while (cur !== null) {
      idxPath.push(cur);
      if (cur === startIdx) break;
      cur = parent[cur];
    }
    if (idxPath[idxPath.length - 1] === startIdx) {
      idxPath.reverse();
      path = idxPath.map((idx) => {
        const coord = fromIndex(idx);
        return { r: coord.r, c: coord.c };
      });
    }
  }

  return { shortest, count: totalCount, path };
}

function buildGraph(grid) {
  const size = grid.length;
  const dirStates = 5;
  const totalStates = size * size * dirStates;
  const adj = Array.from({ length: totalStates }, () => []);
  const rev = Array.from({ length: totalStates }, () => []);
  const toIndex = (r, c, dir) => (r * size + c) * dirStates + dir;

  for (let r = 0; r < size; r += 1) {
    for (let c = 0; c < size; c += 1) {
      const steps = grid[r][c];
      if (!steps) continue;
      for (let prevDir = 0; prevDir < dirStates; prevDir += 1) {
        const allowedDirs = nextDirs(prevDir === 4 ? -1 : prevDir);
        for (const dirIdx of allowedDirs) {
          const dir = DIRS[dirIdx];
          const nr = r + dir.dr * steps;
          const nc = c + dir.dc * steps;
          if (!inBounds(size, nr, nc)) continue;
          const from = toIndex(r, c, prevDir);
          const to = toIndex(nr, nc, dirIdx);
          adj[from].push(to);
          rev[to].push(from);
        }
      }
    }
  }

  return { adj, rev, toIndex, dirStates, size };
}

function bfsDistances(adj, starts) {
  const dist = Array(adj.length).fill(Infinity);
  const queue = [];
  for (const start of starts) {
    dist[start] = 0;
    queue.push(start);
  }
  let head = 0;
  while (head < queue.length) {
    const cur = queue[head++];
    const nextDist = dist[cur] + 1;
    for (const nxt of adj[cur]) {
      if (dist[nxt] === Infinity) {
        dist[nxt] = nextDist;
        queue.push(nxt);
      }
    }
  }
  return dist;
}

function analyzePuzzle(grid, start, end) {
  const graph = buildGraph(grid);
  const startIdx = graph.toIndex(start.r, start.c, 4);
  const endIndices = [0, 1, 2, 3].map((dir) => graph.toIndex(end.r, end.c, dir));
  const distStart = bfsDistances(graph.adj, [startIdx]);
  const distEnd = bfsDistances(graph.rev, endIndices);
  const shortest = Math.min(...endIndices.map((idx) => distStart[idx]));

  let reachable = 0;
  const wrongDepths = [];
  for (let i = 0; i < distStart.length; i += 1) {
    if (distStart[i] === Infinity) continue;
    reachable += 1;
    if (i === startIdx) continue;
    const dEnd = distEnd[i];
    if (dEnd === Infinity || distStart[i] + dEnd > shortest) {
      wrongDepths.push(distStart[i]);
    }
  }

  let maxWrong = 0;
  let avgWrong = 0;
  if (wrongDepths.length > 0) {
    maxWrong = Math.max(...wrongDepths);
    avgWrong = wrongDepths.reduce((sum, v) => sum + v, 0) / wrongDepths.length;
  }
  const spread = wrongDepths.length / Math.max(1, reachable - 1);
  const denom = Math.max(1, shortest);
  const raw = 0.6 * (maxWrong / denom) + 0.3 * (avgWrong / denom) + 0.1 * spread;
  const score = Math.round(Math.min(1, raw) * 100);

  return {
    score,
    maxWrong,
    avgWrong: Math.round(avgWrong * 10) / 10,
    wrongCount: wrongDepths.length,
    reachableCount: reachable,
    shortest
  };
}

function addExtraNumbers(grid, start, end, turns, rng, maxAttempts) {
  const size = grid.length;
  let added = 0;
  let analysis = analyzePuzzle(grid, start, end);

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const r = seededInt(rng, 0, size - 1);
    const c = seededInt(rng, 0, size - 1);
    if ((r === start.r && c === start.c) || (r === end.r && c === end.c)) continue;
    if (grid[r][c] !== null) continue;

    const val = seededInt(rng, 1, size - 1);
    grid[r][c] = val;
    const solved = solvePuzzle(grid, start, end);
    if (solved.shortest === turns && solved.count === 1) {
      const nextAnalysis = analyzePuzzle(grid, start, end);
      if (nextAnalysis.score >= analysis.score) {
        analysis = nextAnalysis;
        added += 1;
      } else {
        grid[r][c] = null;
      }
    } else {
      grid[r][c] = null;
    }
  }

  return { added, analysis };
}

function generatePuzzle(seed, size, targetTurns) {
  const rng = mulberry32(seed >>> 0);
  const maxTurns = size * size - 1;
  const minTurns = Math.min(2, maxTurns);
  const turns = targetTurns ?? seededInt(rng, minTurns, Math.min(maxTurns, size + 2));

  const path = buildPath(rng, size, turns);
  if (!path) return null;

  const grid = createGrid(size);
  for (let i = 0; i < path.length - 1; i += 1) {
    const from = path[i];
    const to = path[i + 1];
    const steps = Math.abs(from.r - to.r) + Math.abs(from.c - to.c);
    grid[from.r][from.c] = steps;
  }

  const start = path[0];
  const end = path[path.length - 1];
  grid[end.r][end.c] = null;

  const solved = solvePuzzle(grid, start, end);
  if (solved.shortest !== turns || solved.count !== 1) return null;

  const extras = addExtraNumbers(grid, start, end, turns, rng, size * size * 2);
  const finalSolved = solvePuzzle(grid, start, end);
  if (finalSolved.shortest !== turns || finalSolved.count !== 1) return null;
  const difficulty = extras.analysis;

  return {
    grid,
    start,
    end,
    turns,
    path: finalSolved.path,
    difficulty
  };
}

function getLayout(size, showSteps) {
  const cell = Math.min(80, Math.max(40, Math.floor(520 / size)));
  const pad = 24;
  const header = showSteps ? 32 : 0;
  const width = pad * 2 + cell * size;
  const height = pad * 2 + cell * size + header;
  return { cell, pad, header, width, height };
}

function setupCanvas(canvas, width, height, dpr) {
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;
  canvas.width = Math.floor(width * dpr);
  canvas.height = Math.floor(height * dpr);
  const ctx = canvas.getContext('2d');
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  return ctx;
}

function drawArrow(ctx, x1, y1, x2, y2) {
  const head = 10;
  const angle = Math.atan2(y2 - y1, x2 - x1);
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(x2 - head * Math.cos(angle - Math.PI / 6), y2 - head * Math.sin(angle - Math.PI / 6));
  ctx.lineTo(x2 - head * Math.cos(angle + Math.PI / 6), y2 - head * Math.sin(angle + Math.PI / 6));
  ctx.closePath();
  ctx.fill();
}

function drawPuzzle(ctx, puzzle, options) {
  const { showSteps, showSolution } = options;
  const { grid, start, end, turns, path } = puzzle;
  const size = grid.length;
  const { cell, pad, header, width, height } = getLayout(size, showSteps);

  ctx.clearRect(0, 0, width, height);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);

  if (showSteps) {
    ctx.fillStyle = '#1a202c';
    ctx.font = 'bold 18px "Trebuchet MS", "Segoe UI", sans-serif';
    ctx.fillText(`Fewest Turns: ${turns}`, pad, pad + 18);
  }

  const top = pad + header;
  ctx.strokeStyle = '#2d3748';
  ctx.lineWidth = 1;
  for (let i = 0; i <= size; i += 1) {
    const x = pad + i * cell;
    const y = top + i * cell;
    ctx.beginPath();
    ctx.moveTo(x, top);
    ctx.lineTo(x, top + cell * size);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(pad, y);
    ctx.lineTo(pad + cell * size, y);
    ctx.stroke();
  }

  if (showSolution && Array.isArray(path)) {
    ctx.strokeStyle = 'rgba(26, 126, 204, 0.85)';
    ctx.fillStyle = 'rgba(26, 126, 204, 0.85)';
    ctx.lineWidth = 3;
    for (let i = 0; i < path.length - 1; i += 1) {
      const a = path[i];
      const b = path[i + 1];
      const x1 = pad + a.c * cell + cell / 2;
      const y1 = top + a.r * cell + cell / 2;
      const x2 = pad + b.c * cell + cell / 2;
      const y2 = top + b.r * cell + cell / 2;
      drawArrow(ctx, x1, y1, x2, y2);
    }
  }

  ctx.font = '600 18px "Trebuchet MS", "Segoe UI", sans-serif';
  ctx.fillStyle = '#111827';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  for (let r = 0; r < size; r += 1) {
    for (let c = 0; c < size; c += 1) {
      const val = grid[r][c];
      if (val === null) continue;
      const x = pad + c * cell + cell / 2;
      const y = top + r * cell + cell / 2;
      ctx.fillText(String(val), x, y);
    }
  }

  const startX = pad + start.c * cell + cell / 2;
  const startY = top + start.r * cell + cell / 2;
  ctx.strokeStyle = '#2f855a';
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.arc(startX, startY, cell * 0.24, 0, Math.PI * 2);
  ctx.stroke();

  const endX = pad + end.c * cell + cell / 2;
  const endY = top + end.r * cell + cell / 2;
  ctx.strokeStyle = '#c53030';
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.arc(endX, endY, cell * 0.24, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(endX, endY, cell * 0.17, 0, Math.PI * 2);
  ctx.stroke();
}

const FewestTurnsGenerator = () => {
  const [gridSize, setGridSize] = useState(6);
  const [turnsInput, setTurnsInput] = useState('');
  const [showTurns, setShowTurns] = useState(true);
  const [seed, setSeed] = useState(0);
  const [status, setStatus] = useState('Use Generate to create a puzzle.');
  const [puzzle, setPuzzle] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [showSolution, setShowSolution] = useState(false);
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !puzzle) return;
    const { width, height } = getLayout(puzzle.grid.length, showTurns);
    const ctx = setupCanvas(canvas, width, height, window.devicePixelRatio || 1);
    drawPuzzle(ctx, puzzle, { showSteps: showTurns, showSolution });
  }, [puzzle, showTurns, showSolution]);

  const parseTurns = () => {
    const trimmed = turnsInput.trim();
    if (trimmed === '') return null;
    const n = parseInt(trimmed, 10);
    return Number.isFinite(n) ? n : null;
  };

  const handleGenerate = async () => {
    const targetTurns = parseTurns();
    if (turnsInput.trim() !== '' && (targetTurns === null || targetTurns < 1)) {
      setStatus('Turns must be a positive number, or left blank.');
      return;
    }

    const size = Math.min(12, Math.max(3, gridSize));
    if (size !== gridSize) setGridSize(size);

    const maxTurns = size * size - 1;
    if (targetTurns !== null && targetTurns > maxTurns) {
      setStatus(`Turns too large for a ${size}x${size} grid (max ${maxTurns}).`);
      return;
    }

    let actualSeed = seed;
    if (!actualSeed) {
      actualSeed = Math.floor(Math.random() * 2 ** 31);
      setSeed(actualSeed);
    }

    setGenerating(true);
    setStatus('Generating puzzle...');
    setShowSolution(false);

    const maxAttempts = 120;
    let bestPuzzle = null;
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      const testSeed = (actualSeed + attempt) >>> 0;
      const puzzleCandidate = generatePuzzle(testSeed, size, targetTurns);
      if (puzzleCandidate) {
        if (!bestPuzzle || puzzleCandidate.difficulty.score > bestPuzzle.difficulty.score) {
          bestPuzzle = { ...puzzleCandidate, seed: testSeed };
        }
        if (bestPuzzle.difficulty.score >= 85) {
          break;
        }
      }
      if (attempt % 10 === 0) {
        await new Promise(resolve => setTimeout(resolve, 0));
      }
    }

    if (bestPuzzle) {
      setSeed(bestPuzzle.seed);
      setPuzzle(bestPuzzle);
      setStatus(`Generated with seed ${bestPuzzle.seed}. Shortest turns: ${bestPuzzle.turns}. Difficulty: ${bestPuzzle.difficulty.score}/100.`);
    } else {
      setStatus('Could not generate a valid puzzle. Try a different seed or turns.');
    }
    setGenerating(false);
  };

  const exportImage = (showSolution) => {
    if (!puzzle) {
      setStatus('Generate a puzzle first.');
      return;
    }
    const { width, height } = getLayout(puzzle.grid.length, showTurns);
    const canvas = document.createElement('canvas');
    const ctx = setupCanvas(canvas, width, height, 2);
    drawPuzzle(ctx, puzzle, { showSteps: showTurns, showSolution });
    canvas.toBlob((blob) => {
      if (!blob) return;
      const link = document.createElement('a');
      const kind = showSolution ? 'solution' : 'task';
      link.download = `fewest-turns-${kind}-${Date.now()}.png`;
      link.href = URL.createObjectURL(blob);
      link.click();
      URL.revokeObjectURL(link.href);
    });
  };

  const renderInfo = () => {
    if (!puzzle) return null;
    const start = `(${puzzle.start.r + 1}, ${puzzle.start.c + 1})`;
    const end = `(${puzzle.end.r + 1}, ${puzzle.end.c + 1})`;
    return (
      <div className="kpi">
        <div>
          <strong>Start</strong>
          <div>{start}</div>
        </div>
        <div>
          <strong>End</strong>
          <div>{end}</div>
        </div>
        <div>
          <strong>Turns</strong>
          <div>{puzzle.turns}</div>
        </div>
        <div>
          <strong>Difficulty</strong>
          <div>{puzzle.difficulty?.score ?? 0}/100</div>
        </div>
        <div>
          <strong>Decoy Depth</strong>
          <div>{puzzle.difficulty?.maxWrong ?? 0}</div>
        </div>
      </div>
    );
  };

  return (
    <div className="fewest-turns-generator">
      <h1>Fewest Turns Generator</h1>
      <p className="subtitle">Each move must turn left or right; only the first move can go in any direction.</p>

      <div className="controls">
        <div className="control-group">
          <div className="control-item">
            <label>Grid Size (3-12)</label>
            <input
              type="number"
              min="3"
              max="12"
              step="1"
              value={gridSize}
              onChange={(e) => {
                const next = parseInt(e.target.value, 10);
                setGridSize(Number.isFinite(next) ? next : 3);
              }}
            />
            <div className="note">N by N grid</div>
          </div>
          <div className="control-item">
            <label>Turns to End (optional)</label>
            <input
              type="number"
              min="1"
              max={gridSize * gridSize - 1}
              value={turnsInput}
              onChange={(e) => setTurnsInput(e.target.value)}
            />
            <div className="note">leave blank to pick from seed</div>
          </div>
          <div className="control-item">
            <label>Seed</label>
            <input
              type="number"
              value={seed}
              onChange={(e) => setSeed(parseInt(e.target.value, 10) || 0)}
            />
            <div className="note">0 uses a random seed</div>
          </div>
          <div className="control-item checkbox-item">
            <label>Include turns in task</label>
            <input
              type="checkbox"
              checked={showTurns}
              onChange={(e) => setShowTurns(e.target.checked)}
            />
            <div className="note">draws the number above the grid</div>
          </div>
        </div>
        <div className="buttons">
          <button className="btn-primary" onClick={handleGenerate} disabled={generating}>
            {generating ? 'Generating...' : 'Generate Puzzle'}
          </button>
          <button className="btn-outline" onClick={() => setShowSolution(!showSolution)} disabled={!puzzle}>
            {showSolution ? 'Hide Solution' : 'Show Solution'}
          </button>
          <button className="btn-secondary" onClick={() => exportImage(false)} disabled={!puzzle}>
            Export Task Image
          </button>
          <button className="btn-secondary" onClick={() => exportImage(true)} disabled={!puzzle}>
            Export Solution Image
          </button>
        </div>
      </div>

      <div className="canvas-wrap">
        <canvas ref={canvasRef} />
      </div>

      <div className="info">
        <div>{status}</div>
        {renderInfo()}
      </div>
    </div>
  );
};

export default FewestTurnsGenerator;
